// @ts-nocheck
/*
 * Port of views/dashboard/voice-plan/VoicePlanDetailsPanel.js (200 lines).
 * BYTE-IDENTICAL — plan hero header, plan-legs table, plan-summary aside;
 * "Edit Plan" button visible when canEdit (allocated plans open in add-leg-only mode).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FaWaveSquare,
  FaCopy,
  FaListAlt,
  FaRupeeSign,
  FaShieldAlt,
  FaPencilAlt,
} from 'react-icons/fa';
import {
  formatVoicePlanId,
  formatDurationLabel,
  formatPriceDisplay,
  getUniqueCallTypes,
  flattenPlansToArray,
} from '@/utilities/voicePlanUtils';
import { CallTypeIcon, LegTypeDisplay, PlanStatusBadge } from '@/features/voice-plan/voice-plan-shared';

function SummaryItem({ iconClass, Icon, label, value }) {
  return (
    <div className="vp-summary-item">
      <div className={`vp-summary-item__icon ${iconClass}`}>
        <Icon />
      </div>
      <div className="vp-summary-item__content">
        <div className="vp-summary-item__label">{label}</div>
        <div className="vp-summary-item__value">{value}</div>
      </div>
    </div>
  );
}

export default function VoicePlanDetailsPanel({ plan, enabled, canEdit = false, compact = false }) {
  const navigate = useNavigate();

  if (!plan) return null;

  const isEnabled = enabled ?? plan.plan_status == 1;
  const callTypes = getUniqueCallTypes(plan.plans);
  const planLegs = flattenPlansToArray(plan.plans);
  const createdDate = plan.req_date
    ? new Date(plan.req_date).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    : '—';

  const copyId = () => {
    navigator.clipboard?.writeText(formatVoicePlanId(plan.id));
    toast.success('Plan ID copied');
  };

  return (
    <div className={compact ? 'vp-details-panel vp-details-panel--compact' : 'vp-details-panel'}>
      <div className="vp-detail-hero">
        <div className="vp-detail-hero__left">
          <div className="vp-detail-hero__icon">
            <FaWaveSquare />
          </div>
          <div>
            <div className="vp-detail-hero__title-row">
              <h2 className="vp-detail-hero__title">{plan.plan_name}</h2>
              <PlanStatusBadge enabled={isEnabled} large />
            </div>
            <div className="vp-detail-hero__id">
              ID: {formatVoicePlanId(plan.id)}
              <button type="button" onClick={copyId} title="Copy ID">
                <FaCopy size={12} />
              </button>
            </div>
            <p className="vp-detail-hero__desc">
              Voice plan configuration for inbound, outbound, and missed call billing.
            </p>
          </div>
        </div>

        <div className="vp-detail-hero__meta">
          <div className="vp-detail-meta-item">
            <div className="vp-detail-meta-item__label">Created By</div>
            <div className="vp-detail-meta-item__value">{plan.user?.username || '—'}</div>
          </div>
          <div className="vp-detail-meta-item">
            <div className="vp-detail-meta-item__label">Created Date</div>
            <div className="vp-detail-meta-item__value">{createdDate}</div>
          </div>
        </div>
      </div>

      <div className="vp-detail-body">
        <div className="vp-detail-legs">
          <div className="vp-detail-legs__header">
            <h5>Plan Legs ({planLegs.length})</h5>
            {canEdit && (
              <button
                type="button"
                onClick={() => navigate(`/voice-plan/edit/${plan.id}`)}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-primary/25 bg-primary/10 px-3 text-xs font-semibold text-primary transition-colors hover:bg-primary/15 cursor-pointer"
              >
                <FaPencilAlt />
                Edit Plan
              </button>
            )}
          </div>

          <table className="vp-detail-table">
            <thead>
              <tr>
                <th>Call Type</th>
                <th>Leg Type</th>
                <th>Pulse Duration</th>
                <th>Pulse Price (Paisa)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {planLegs.map((leg, index) => {
                const type = String(leg.plan_type || '').toUpperCase();
                const durationClass =
                  type === 'MISSED'
                    ? 'vp-duration-pill--na'
                    : `vp-duration-pill--${type.toLowerCase()}`;

                return (
                  <tr key={index}>
                    <td>
                      <span className="vp-detail-call-type">
                        <CallTypeIcon type={type} />
                        {type}
                      </span>
                    </td>
                    <td>
                      <LegTypeDisplay legType={leg.leg_type} />
                    </td>
                    <td>
                      <span className={`vp-duration-pill ${durationClass}`}>
                        {type === 'MISSED' ? 'N/A' : formatDurationLabel(leg.pulse_duration)}
                      </span>
                    </td>
                    <td>{formatPriceDisplay(leg.pulse_price)}</td>
                    <td>
                      <PlanStatusBadge enabled={isEnabled} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <aside className="vp-detail-summary">
          <h5 className="vp-detail-summary__title">Plan Summary</h5>
          <SummaryItem
            iconClass="vp-summary-icon--legs"
            Icon={FaListAlt}
            label="Total Legs"
            value={String(planLegs.length)}
          />
          <SummaryItem
            iconClass="vp-summary-icon--types"
            Icon={FaWaveSquare}
            label="Call Types"
            value={callTypes.join(', ') || '—'}
          />
          <SummaryItem
            iconClass="vp-summary-icon--currency"
            Icon={FaRupeeSign}
            label="Default Currency"
            value="Paisa (INR)"
          />
          <SummaryItem
            iconClass="vp-summary-icon--status"
            Icon={FaShieldAlt}
            label="Plan Status"
            value={isEnabled ? 'ENABLED' : 'DISABLED'}
          />
        </aside>
      </div>
    </div>
  );
}
